import { Check, ChevronRight, CloudUpload } from "lucide-react";
import type { CSSProperties } from "react";
import { getUploadExperienceCopy } from "../../data/uploadExperienceCopy";
import type { ChunkedUploadProgress } from "../../hooks/useChunkedUpload";
import { useTranslation } from "../../i18n";

const stageKeys = {
	preparing: "uploadModal.stagePreparing",
	sending: "uploadModal.stageSending",
	waiting: "uploadModal.stageWaiting",
	finalizing: "uploadModal.stageFinalizing",
	link: "uploadModal.stageLink",
} as const;

export function UploadTransferMeter({ file, progress }: { file: File; progress: ChunkedUploadProgress | null }) {
	const { t, locale } = useTranslation();
	const copy = getUploadExperienceCopy(locale);
	const stage = progress?.stage ?? "preparing";
	const percentage = progress?.percentage ?? 0;
	const current = stage === "link" ? 2 : stage === "finalizing" ? 1 : 0;
	const moving = stage === "sending";
	const remaining = progress?.remainingTimeSeconds ?? 0;
	return (
		<div className="upload-transfer-meter" data-stage={stage}>
			<div className="upload-transfer-file">
				<CloudUpload size={20} aria-hidden="true" />
				<span>{file.name}</span>
			</div>
			<output className="creation-stage" aria-live="polite">
				{t(stageKeys[stage])}
			</output>
			<div className="upload-transfer-readout">
				<div className="creation-progress-number">
					{percentage}
					<span>%</span>
				</div>
				{moving && !!progress?.uploadSpeedMBps && (
					<div className="upload-transfer-speed">
						<strong>{progress.uploadSpeedMBps.toFixed(1)}</strong>
						<span>MiB/s</span>
					</div>
				)}
			</div>
			<div
				className={`upload-transfer-track${moving ? " is-moving" : ""}`}
				style={{ "--upload-progress": `${percentage}%` } as CSSProperties}
			>
				<progress className="sr-only" max={100} value={percentage} aria-label={t("uploadModal.uploading")} />
				<div className="upload-transfer-fill" aria-hidden="true" />
			</div>
			<div className="upload-transfer-details">
				<p className="creation-progress-bytes">
					{(progress?.uploadedSizeMB ?? 0).toFixed(2)} / {(file.size / 1024 / 1024).toFixed(2)} MiB
				</p>
				{moving && remaining > 0 && (
					<span>
						{copy.remaining} {remaining >= 60 ? Math.ceil(remaining / 60) : remaining}{" "}
						{remaining >= 60 ? copy.minutes : copy.seconds}
					</span>
				)}
			</div>
			<ol className="upload-transfer-stages">
				{[copy.transfer, copy.save, copy.link].map((label, index) => (
					<li
						key={label}
						className={index < current ? "is-complete" : index === current ? "is-current" : ""}
						aria-current={index === current ? "step" : undefined}
					>
						{index < current ? (
							<Check size={16} aria-hidden="true" />
						) : (
							<span className="upload-stage-dot" aria-hidden="true" />
						)}
						<span>{label}</span>
						{index < 2 && <ChevronRight size={14} aria-hidden="true" />}
					</li>
				))}
			</ol>
		</div>
	);
}
